import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../components/ui/SafeIcon'
import {
  searchProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  uploadProductImage,
  getCategories,
  getSuppliers
} from '../services/supabaseApi'

const { FiPlus, FiEdit, FiTrash2, FiSave, FiX, FiUpload } = FiIcons

function AdminProductsPage() {
  const { user, isAuthenticated } = useAuthStore()
  const navigate = useNavigate()

  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [suppliers, setSuppliers] = useState([])
  const [loading, setLoading] = useState(true)
  const [editingProduct, setEditingProduct] = useState(null)
  const [showForm, setShowForm] = useState(false)

  const [formData, setFormData] = useState({
    name_de: '',
    name_en: '',
    name_fr: '',
    description_de: '',
    description_en: '',
    description_fr: '',
    price: 0,
    original_price: 0,
    min_order_quantity: 1,
    stock_quantity: 0,
    category_id: '',
    supplier_id: '',
    trade_assurance: false,
    is_verified: false,
    is_featured: false
  })

  // ✅ Check Admin-Rechte
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    // TODO: Implement proper admin check
    // if (!user?.isAdmin) {
    //   navigate('/')
    //   return
    // }
    loadData()
  }, [isAuthenticated, navigate])

  const loadData = async () => {
    setLoading(true)
    try {
      const [productsData, categoriesData, suppliersData] = await Promise.all([
        searchProducts('', { limit: 100 }),
        getCategories(),
        getSuppliers()
      ])
      setProducts(productsData.products)
      setCategories(categoriesData)
      setSuppliers(suppliersData)
    } catch (error) {
      console.error('Load data failed:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      if (editingProduct) {
        await updateProduct(editingProduct.id, formData)
      } else {
        await createProduct(formData)
      }
      setShowForm(false)
      setEditingProduct(null)
      setFormData({
        name_de: '',
        name_en: '',
        name_fr: '',
        description_de: '',
        description_en: '',
        description_fr: '',
        price: 0,
        original_price: 0,
        min_order_quantity: 1,
        stock_quantity: 0,
        category_id: '',
        supplier_id: '',
        trade_assurance: false,
        is_verified: false,
        is_featured: false
      })
      await loadData()
    } catch (error) {
      console.error('Save product failed:', error)
      alert('Fehler beim Speichern')
    }
  }

  const handleEdit = (product) => {
    setEditingProduct(product)
    setFormData({
      name_de: product.name_de,
      name_en: product.name_en,
      name_fr: product.name_fr,
      description_de: product.description_de || '',
      description_en: product.description_en || '',
      description_fr: product.description_fr || '',
      price: product.price,
      original_price: product.original_price || 0,
      min_order_quantity: product.min_order_quantity,
      stock_quantity: product.stock_quantity,
      category_id: product.category_id,
      supplier_id: product.supplier_id,
      trade_assurance: product.trade_assurance,
      is_verified: product.is_verified,
      is_featured: product.is_featured
    })
    setShowForm(true)
  }

  const handleDelete = async (id) => {
    if (!confirm('Wirklich löschen?')) return
    try {
      await deleteProduct(id)
      await loadData()
    } catch (error) {
      console.error('Delete failed:', error)
      alert('Fehler beim Löschen')
    }
  }

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Laden...</div>
  }

  return (
    <>
      <Helmet>
        <title>Produkte verwalten | Admin | 1688Euro.com</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Produkte verwalten</h1>
            <button
              onClick={() => {
                setShowForm(true)
                setEditingProduct(null)
                setFormData({
                  name_de: '',
                  name_en: '',
                  name_fr: '',
                  description_de: '',
                  description_en: '',
                  description_fr: '',
                  price: 0,
                  original_price: 0,
                  min_order_quantity: 1,
                  stock_quantity: 0,
                  category_id: '',
                  supplier_id: '',
                  trade_assurance: false,
                  is_verified: false,
                  is_featured: false
                })
              }}
              className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              <SafeIcon icon={FiPlus} className="w-5 h-5 mr-2" />
              Neues Produkt
            </button>
          </div>

          {/* Form Modal */}
          {showForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">
                    {editingProduct ? 'Produkt bearbeiten' : 'Neues Produkt'}
                  </h2>
                  <button onClick={() => setShowForm(false)}>
                    <SafeIcon icon={FiX} className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Multilingual Names */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name (DE) *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name_de}
                        onChange={(e) => handleChange('name_de', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name (EN) *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name_en}
                        onChange={(e) => handleChange('name_en', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name (FR) *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name_fr}
                        onChange={(e) => handleChange('name_fr', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  {/* Descriptions */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Beschreibung (DE)
                      </label>
                      <textarea
                        value={formData.description_de}
                        onChange={(e) => handleChange('description_de', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Beschreibung (EN)
                      </label>
                      <textarea
                        value={formData.description_en}
                        onChange={(e) => handleChange('description_en', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Beschreibung (FR)
                      </label>
                      <textarea
                        value={formData.description_fr}
                        onChange={(e) => handleChange('description_fr', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  {/* Pricing & Inventory */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Preis (EUR) *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        required
                        value={formData.price}
                        onChange={(e) => handleChange('price', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        UVP (EUR)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.original_price}
                        onChange={(e) => handleChange('original_price', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        MOQ *
                      </label>
                      <input
                        type="number"
                        required
                        value={formData.min_order_quantity}
                        onChange={(e) => handleChange('min_order_quantity', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Lagerbestand
                      </label>
                      <input
                        type="number"
                        value={formData.stock_quantity}
                        onChange={(e) => handleChange('stock_quantity', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  {/* Relations */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Kategorie *
                      </label>
                      <select
                        required
                        value={formData.category_id}
                        onChange={(e) => handleChange('category_id', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        <option value="">-- Auswählen --</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Lieferant *
                      </label>
                      <select
                        required
                        value={formData.supplier_id}
                        onChange={(e) => handleChange('supplier_id', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        <option value="">-- Auswählen --</option>
                        {suppliers.map(sup => (
                          <option key={sup.id} value={sup.id}>{sup.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Flags */}
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.trade_assurance}
                        onChange={(e) => handleChange('trade_assurance', e.target.checked)}
                        className="mr-2"
                      />
                      Trade Assurance
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_verified}
                        onChange={(e) => handleChange('is_verified', e.target.checked)}
                        className="mr-2"
                      />
                      Verifiziert
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_featured}
                        onChange={(e) => handleChange('is_featured', e.target.checked)}
                        className="mr-2"
                      />
                      Featured
                    </label>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-4 pt-4 border-t">
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Abbrechen
                    </button>
                    <button
                      type="submit"
                      className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                    >
                      <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                      Speichern
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Products Table */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bild</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Preis</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kategorie</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {products.map(product => (
                  <tr key={product.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <img
                        src={product.image || 'https://via.placeholder.com/50'}
                        alt={product.name}
                        className="w-12 h-12 object-cover rounded"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{product.name}</div>
                      <div className="text-xs text-gray-500">ID: {product.id}</div>
                    </td>
                    <td className="px-4 py-3 font-medium">{product.price.toFixed(2)} €</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{product.category?.name_de}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        {product.is_featured && (
                          <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Featured</span>
                        )}
                        {product.trade_assurance && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">TA</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleEdit(product)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <SafeIcon icon={FiEdit} className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(product.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {products.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                Keine Produkte vorhanden. Erstellen Sie das erste Produkt!
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default AdminProductsPage